import { GanttDisplayOptions } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import { WbsItemStatus } from '../../wbsItemStatus'

export type LabelMode = 'inside' | 'right' | 'left' | 'hover'

export type WbsItemForDisplay = {
  displayName?: string
  status?: WbsItemStatus
  responsible?: { name?: string; iconUrl?: string }
}

type LabelModeParams = {
  isV2: boolean
  isBarVisible: boolean
  isParentType: boolean
  isDragging: boolean
  cellWidth: number
  barWidthPercent: number
  rightSpacePercent: number
  leftSpacePercent: number
  displayOptions?: GanttDisplayOptions
  wbsItemForDisplay?: WbsItemForDisplay
  progressRate: number
  hasCumulation: boolean
}

/** テキスト幅をピクセルで推定（全角≒fontSize、半角≒fontSize*0.6） */
const estimateTextWidth = (text: string, fontSize: number): number => {
  let width = 0
  for (const char of text) {
    width += char.charCodeAt(0) > 0x7f ? fontSize : fontSize * 0.6
  }
  return width
}

/**
 * ラベル配置モードを判定する。
 *
 * 末端タイプ（deliverable/task）: right → hover（バー外側に固定、左スペースへのフォールバックなし）
 * 親タイプ（workgroup/process/deliverableList）: inside → right → left → 最も広いスペース
 */
export const determineLabelMode = ({
  isV2,
  isBarVisible,
  isParentType,
  isDragging,
  cellWidth,
  barWidthPercent,
  rightSpacePercent,
  leftSpacePercent,
  displayOptions,
  wbsItemForDisplay,
  progressRate,
  hasCumulation,
}: LabelModeParams): LabelMode => {
  if (!isV2 || !isBarVisible) return 'inside'
  if (isDragging) return 'hover'
  if (cellWidth === 0) return 'hover'

  const fontSize = isParentType ? 12 : 10
  let contentWidth = 0
  if (
    !isParentType &&
    displayOptions?.showStatus &&
    wbsItemForDisplay?.status
  ) {
    contentWidth += (isParentType ? 14 : 10) + 3
  }
  if (displayOptions?.showName && wbsItemForDisplay?.displayName) {
    contentWidth += estimateTextWidth(wbsItemForDisplay.displayName, fontSize)
  }
  if (
    !isParentType &&
    displayOptions?.showAssignee &&
    wbsItemForDisplay?.responsible
  ) {
    contentWidth += (isParentType ? 20 : 16) + 3
  }
  if (displayOptions?.showProgressRate && hasCumulation) {
    contentWidth +=
      estimateTextWidth(`${(progressRate * 100).toFixed(1)}%`, fontSize) + 4
  }

  const insidePx = (barWidthPercent / 100) * cellWidth - 8 // padding 4px * 2
  const rightPx = (rightSpacePercent / 100) * cellWidth - 16 // padding + margin
  const leftPx = (leftSpacePercent / 100) * cellWidth - 16 // padding + margin

  // 末端タイプ（deliverable/task）: バー内には表示しない
  if (!isParentType) {
    if (rightPx > 0) return 'right' // 少しでも右スペースがあれば省略表示で right
    return 'hover' // 右スペースが実質ゼロ → ホバーTooltipのみ
  }

  // 親タイプ（workgroup/process/deliverableList）: 従来ロジック
  if (contentWidth <= insidePx) return 'inside'
  if (contentWidth <= rightPx) return 'right'
  if (contentWidth <= leftPx) return 'left'
  // どれにも収まらない場合は最も広いスペースに truncate 表示
  if (rightPx >= insidePx && rightPx >= leftPx) return 'right'
  if (leftPx >= insidePx) return 'left'
  return 'inside'
}
